"use client";

import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { TrendingUp, Building2, Users, Star } from "lucide-react";

/* ── Animated counter ── */
const CountUp = ({ end, suffix = "", prefix = "", duration = 2 }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (!inView) return;

        let start = 0;
        const step = end / (duration * 60);

        const id = setInterval(() => {
            start += step;

            if (start >= end) {
                setValue(end);
                clearInterval(id);
            } else {
                setValue(Math.floor(start));
            }
        }, 1000 / 60);

        return () => clearInterval(id);
    }, [inView, end, duration]);

    return <span ref={ref}>{prefix}{value}{suffix}</span>;
};

/* ── Stats data ── */
const stats = [
    {
        icon: TrendingUp,
        value: 13,
        suffix: "%",
        label: "Growth",
        desc: "Projected CAGR for India's hospitality sector through 2030",
        color: "hsl(152 56% 45%)",
    },
    {
        icon: Building2,
        value: 32,
        suffix: " Lac Cr",
        prefix: "₹",
        label: "Industry Size",
        desc: "India's travel & hospitality market projected by 2034 (WTTC)",
        color: "#F5A400",
    },
    {
        icon: Users,
        value: 50,
        suffix: " Lac+",
        label: "Job Opportunities",
        desc: "Direct jobs expected in India's hospitality sector by 2030",
        color: "hsl(152 56% 45%)",
    },
    {
        icon: Star,
        value: 0,
        suffix: "",
        label: "Resilient Demand",
        desc: "Hospitality is among India's top 10 employment sectors, consistently growing post-pandemic",
        color: "#F5A400",
        isText: true,
        textValue: "Essential Sector",
    },
];

/* ── Trend Graph ── */
const trendPoints = [
    { year: "2022", value: 72 },
    { year: "2024", value: 105 },
    { year: "2026", value: 145 },
    { year: "2028", value: 195 },
    { year: "2030", value: 250 },
];

const TrendGraph = () => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });

    const w = 280, h = 160, px = 36, py = 10;
    const plotW = w - px - 8, plotH = 118;
    const minVal = 70, maxVal = 250;

    const points = trendPoints.map((p, i) => ({
        x: px + 14 + (i / (trendPoints.length - 1)) * (plotW - 14),
        y: py + plotH - ((p.value - minVal) / (maxVal - minVal)) * plotH,
        ...p,
    }));

    const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
    const areaPath =
        `${linePath} L${points[points.length - 1].x},${py + plotH} L${points[0].x},${py + plotH} Z`;

    const gridYValues = [250, 190, 130, 70];

    return (
        <svg ref={ref} viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
            <defs>
                <linearGradient id="areaGradNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(152 56% 45%)" stopOpacity="0.18" />
                    <stop offset="100%" stopColor="hsl(152 56% 45%)" stopOpacity="0.02" />
                </linearGradient>
            </defs>

            <line x1={px} y1={py} x2={px} y2={py + plotH} stroke="hsl(0 0% 90%)" strokeWidth="0.8" />
            <line x1={px} y1={py + plotH} x2={w - 8} y2={py + plotH} stroke="hsl(0 0% 80%)" strokeWidth="0.8" />

            {gridYValues.map((val) => {
                const yPos = py + plotH - ((val - minVal) / (maxVal - minVal)) * plotH;
                return (
                    <g key={val}>
                        <line
                            x1={px}
                            x2={w - 8}
                            y1={yPos}
                            y2={yPos}
                            stroke="hsl(0 0% 91%)"
                            strokeWidth="0.5"
                            strokeDasharray="3 3"
                        />
                        <text
                            x={px - 6}
                            y={yPos + 3.5}
                            textAnchor="end"
                            fontSize="10"
                        >
                            {val}
                        </text>
                    </g>
                );
            })}

            <motion.path
                d={areaPath}
                fill="url(#areaGradNew)"
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ duration: 1, delay: 0.5 }}
            />

            <motion.path
                d={linePath}
                fill="none"
                stroke="hsl(152 56% 45%)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={inView ? { pathLength: 1 } : {}}
                transition={{ duration: 1.5, ease: "easeOut" }}
            />

            {points.map((p, i) => (
                <motion.circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r="3.5"
                    fill="white"
                    stroke="hsl(152 56% 45%)"
                    strokeWidth="1.8"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={inView ? { scale: 1, opacity: 1 } : {}}
                    transition={{ delay: 0.3 + i * 0.15, duration: 0.3 }}
                />
            ))}

            {points.map((p) => (
                <text
                    key={p.year}
                    x={p.x}
                    y={h - 4}
                    textAnchor="middle"
                    fontSize="10"
                >
                    {p.year}
                </text>
            ))}
        </svg>
    );
};

/* ── Main Section ── */
const IndustryGrowth = () => (
    <section className="relative bg-[hsl(50_14%_96%)] overflow-hidden 
    bg-max-w-(--max-page-width) mx-auto px-(--container-padding) w-full py-16 ">
        <div className="w-full relative z-10 ">

            <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-10"
            >
                <p className="text-[#F5A400] uppercase tracking-[4px] text-xs font-semibold mb-4">
                    Industry Insights
                </p>

                <h2 className="font-display text-xl md:text-2xl lg:text-3xl font-bold leading-tight w-full max-w-2xl mx-auto text-center">
                    Why is a Career in Hospitality & Hotel sector a{" "}
                    <span className="text-[#F5A400]">Smart Career Choice</span>?
                </h2>
            </motion.div>

            {/* Grid */}
            <div className="grid lg:grid-cols-[1fr_1fr_1.4fr] gap-4 items-start">

                {/* Stats */}
                <div className="lg:col-span-2 grid grid-cols-2 gap-4">
                    {stats.map((item, i) => (
                        <motion.div
                            key={item.label}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.4, delay: i * 0.08 }}
                            className="group rounded-[14px]  bg-white p-5 md:p-6"
                        >
                            <div className="mb-3.5">
                                <item.icon size={22} style={{ color: item.color }} />
                            </div>

                            <p className="text-[28px] font-bold mb-1.5" style={{ color: item.color }}>
                                {item.isText ? (
                                    <span className="text-[28px]">{item.textValue}</span>
                                ) : (
                                    <CountUp
                                        end={item.value}
                                        suffix={item.suffix}
                                        prefix={item.prefix || ""}
                                    />
                                )}
                            </p>

                            <p className="text-md font-semibold">{item.label}</p>
                            <p className="text-sm text-gray-500">{item.desc}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Chart */}
                <motion.div
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="rounded-[14px]  bg-white p-5 md:p-6"
                >
                    <div className="flex items-center gap-1.5 mb-0.5">
                        <TrendingUp size={14} />
                        <p className="text-md font-semibold">
                            India Hospitality Revenue Growth
                        </p>
                    </div>

                    <p className="text-sm mb-3.5">
                        Rising trend · 2022–2030 projection
                    </p>

                    <TrendGraph />

                    <div className="mt-3 pt-2 border-t border-border/20">
                        <p className="text-[13px] text-gray-500 leading-relaxed">
                            India's hospitality revenue is projected to <strong className="text-foreground font-semibold">surge from ~$72B to $250B</strong> by 2030, driven by rising domestic travel, global tourism, and MICE industry expansion. Source: WTTC / CII reports.
                        </p>
                    </div>
                </motion.div>

            </div>
        </div>
    </section>
);

export default IndustryGrowth;